"use client";
import Select, { MultiValue, SingleValue } from "react-select";
import { useEffect, useState, Ref } from "react";
import StateManagedSelect from "react-select/base";
import tinycolor from "tinycolor2";
import { ColorUtils } from "../utils/ColorUtils";

export interface Tag {
  value: string;
  label: string;
  color?: string;
}

interface TagSelectorProps {
  ref?: Ref<StateManagedSelect>; //Ref<Select<Tag, boolean, GroupBase<Tag>>> | undefined
  isMulti: boolean;
  options: Tag[];
  defaultValue: Tag[];
  onChange: (value: MultiValue<Tag> | SingleValue<Tag>) => void;
  placeholder: string;
  isInvalid?: boolean;
  width?: string;
}

export default function TagSelector({
  ref,
  isMulti,
  options,
  defaultValue,
  onChange,
  placeholder,
  isInvalid = false,
  width = "100%",
}: TagSelectorProps) {
  const [selectedTags, setSelectedTags] = useState<Tag[]>([]);
  useEffect(() => {
    if (defaultValue) {
      setSelectedTags(defaultValue);
    }
  }, [defaultValue]);

  const dot = (color = "transparent") => ({
    alignItems: "center",
    display: "flex",

    ":before": {
      backgroundColor: color,
      borderRadius: 10,
      content: '" "',
      display: "block",
      marginRight: 8,
      height: 10,
      width: 10,
    },
  });
  const customStyles = {
    control: (base: any, state: any) => ({
      ...base,
      borderColor: isInvalid
        ? "#dc3545"
        : state.isFocused
        ? "#2684FF"
        : "#ced4da",
      boxShadow: isInvalid
        ? "0 0 0 1px #dc3545"
        : state.isFocused
        ? "0 0 0 1px #2684FF"
        : "none",
      "&:hover": {
        // borderColor: state.isFocused ? '#2684FF' : '#a1a7ae'
        borderColor: isInvalid
          ? "#dc3545"
          : state.isFocused
          ? "#2684FF"
          : "#a1a7ae",
      },
      // width: width,
    }),
    singleValue: (base: any, state: any) => {
      const dotObj = state?.data?.color ? dot(state?.data?.color) : {};
      return {
        ...base,
        ...dotObj,
      };
    },
    multiValue: (base: any, state: any) => ({
      ...base,
      backgroundColor: tinycolor(state.data.color).toRgbString(), //setAlpha(0.8)
      color: ColorUtils.getTextColor(state.data.color)  ?? "#000",
    }),
    multiValueLabel: (base: any) => ({
      ...base,
      color: "inherit",
    }),
    menu: (base: any) => ({ ...base, zIndex: 999999 }),
  };

  return (
    <Select
      ref={ref}
      isMulti={isMulti}
      options={options ?? []}
      value={selectedTags}
      onChange={(newValue: unknown) => {
        setSelectedTags(newValue as Tag[]);
        if (typeof onChange === "function") {
          onChange(newValue as Tag[]);
        }
      }}
      // styles={customStyles}
      placeholder={placeholder}
      closeMenuOnSelect={false}
      styles={customStyles}
    />
  );
}

export const getSelectedOptionsById = (
  options: any[],
  id: number | number[],
  isSingle = false
) => {
  if (!id || id.toString().length === 0) {
    return [];
  }

  if (isSingle && Number.isNaN(Number(id))) {
    return [];
  }

  if (!options || !Array.isArray(options) || options.length === 0) {
    return [];
  }

  if (Array.isArray(id)) {
    return options.filter((x) => id.map((x) => x.toString()).includes(x.value));
  }

  const findResults = !isSingle
    ? options.filter((x) => x.value.toString() === id.toString())
    : options.find((x) => x.value.toString() === id.toString());
  return findResults;
};

export const getSelectedOptions = (
  options: any[],
  value: string | string[],
  isSingle = false,
) => {
  if (!value || value.toString().length === 0) {
    return (!isSingle) ? [] : {};
  }

  if (!options || !Array.isArray(options) || options.length === 0) {
    return (!isSingle) ? [] : {};
  }

  if (Array.isArray(value)) {
    return options.filter((x) => value.map((x) => x.toString()).includes(x.value));
  }
  return options.find((x) => x.value.toString() === value.toString());
};
